package songwritersnotebook.app.screens

import androidx.compose.animation.*
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Email
import androidx.compose.material.icons.filled.Lock
import androidx.compose.material.icons.filled.LockOpen
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.components.WebNavHeader
import songwritersnotebook.app.viewmodels.MainViewModel
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.Job
import org.jetbrains.compose.resources.painterResource
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_logo_fg
import songwritersnotebookapp.composeapp.generated.resources.ic_google

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AuthScreen(
    onBack: () -> Unit,
    onSuccess: () -> Unit,
    mainViewModel: MainViewModel,
    onGoogleSignIn: () -> Unit,
    onFeatures: () -> Unit,
    onPricing: () -> Unit,
    onRoadmap: () -> Unit,
    onHome: () -> Unit
) {
    var email by remember { mutableStateOf("") }
    var password by remember { mutableStateOf("") }
    var isSignUp by remember { mutableStateOf(false) }
    var passwordVisible by remember { mutableStateOf(false) }
    var isLoading by remember { mutableStateOf(false) }
    var showCancelButton by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var isSuccessMessage by remember { mutableStateOf(false) }
    var authJob by remember { mutableStateOf<Job?>(null) }

    val scope = rememberCoroutineScope()

    // Safety Timeout for Loading State
    LaunchedEffect(isLoading) {
        if (isLoading) {
            showCancelButton = false
            delay(10000) // Show cancel after 10 seconds
            showCancelButton = true
            delay(20000) // Force stop after another 20 seconds (30 total)
            if (isLoading) {
                isLoading = false
                authJob?.cancel()
                error = "Request timed out. Please check your internet connection."
            }
        } else {
            showCancelButton = false
        }
    }
    
    val isWeb = remember { songwritersnotebook.app.getPlatform().name.contains("Web", ignoreCase = true) }
    val currentUser by mainViewModel.currentUser.collectAsState()

    // Redirect on success
    LaunchedEffect(currentUser) {
        if (currentUser != null) {
            onSuccess()
        }
    }

    Scaffold(
        topBar = {
            if (isWeb) {
                WebNavHeader(
                    mainViewModel = mainViewModel,
                    onSignIn = { /* Already here */ },
                    onFeatures = onFeatures,
                    onPricing = onPricing,
                    onRoadmap = onRoadmap,
                    onHome = onHome,
                    selectedRoute = "auth"
                )
            } else {
                TopAppBar(
                    title = { Text(if (isSignUp) "Create Account" else "Sign In") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                        }
                    }
                )
            }
        }
    ) { paddingValues ->
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .then(
                    if (isWeb) {
                        Modifier.background(
                            Brush.verticalGradient(
                                colors = listOf(
                                    MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.1f),
                                    MaterialTheme.colorScheme.background
                                )
                            )
                        )
                    } else {
                        Modifier.background(MaterialTheme.colorScheme.background)
                    }
                ),
            contentAlignment = Alignment.Center
        ) {
            Card(
                modifier = Modifier
                    .widthIn(max = 450.dp)
                    .fillMaxWidth()
                    .padding(24.dp)
                    .verticalScroll(rememberScrollState()),
                shape = RoundedCornerShape(28.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                elevation = CardDefaults.cardElevation(defaultElevation = if (isWeb) 8.dp else 0.dp)
            ) {
                Column(
                    modifier = Modifier.padding(32.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    // Logo (Web Only)
                    if (isWeb) {
                        Image(
                            painter = painterResource(Res.drawable.ic_logo_fg),
                            contentDescription = null,
                            modifier = Modifier.size(64.dp),
                            colorFilter = androidx.compose.ui.graphics.ColorFilter.tint(MaterialTheme.colorScheme.primary)
                        )
                        Spacer(Modifier.height(16.dp))
                    }

                    Text(
                        text = if (isSignUp) "Join Songwriter's Notebook" else "Welcome",
                        style = MaterialTheme.typography.headlineSmall,
                        fontWeight = FontWeight.Black,
                        textAlign = TextAlign.Center
                    )
                    
                    Text(
                        text = if (isSignUp) "Start collaborating on your songs today." else "Sign in to sync your creativity.",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.padding(top = 8.dp)
                    )

                    Spacer(Modifier.height(32.dp))

                    // Google Sign In (Primary)
                    Button(
                        onClick = {
                            if (showCancelButton) {
                                isLoading = false
                                mainViewModel.updateIsSyncing(false)
                            } else {
                                isLoading = true
                                onGoogleSignIn()
                            }
                        },
                        modifier = Modifier.fillMaxWidth().height(56.dp),
                        shape = RoundedCornerShape(16.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = if (showCancelButton) MaterialTheme.colorScheme.errorContainer else MaterialTheme.colorScheme.surfaceVariant,
                            contentColor = if (showCancelButton) MaterialTheme.colorScheme.onErrorContainer else MaterialTheme.colorScheme.onSurfaceVariant
                        ),
                        enabled = true // Always enabled so user can tap Cancel
                    ) {
                        if (isLoading) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(24.dp),
                                    strokeWidth = 2.dp,
                                    color = if (showCancelButton) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.primary
                                )
                                if (showCancelButton) {
                                    Spacer(Modifier.width(12.dp))
                                    Text("Cancel", fontWeight = FontWeight.Bold)
                                }
                            }
                        } else {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    painter = painterResource(Res.drawable.ic_google),
                                    contentDescription = null,
                                    modifier = Modifier.size(20.dp),
                                    tint = Color.Unspecified
                                )
                                Spacer(Modifier.width(12.dp))
                                Text("Continue with Google", fontWeight = FontWeight.Bold)
                            }
                        }
                    }

                    Spacer(Modifier.height(24.dp))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        HorizontalDivider(modifier = Modifier.weight(1f))
                        Text(
                            "OR",
                            modifier = Modifier.padding(horizontal = 16.dp),
                            style = MaterialTheme.typography.labelSmall,
                            color = MaterialTheme.colorScheme.outline
                        )
                        HorizontalDivider(modifier = Modifier.weight(1f))
                    }

                    Spacer(Modifier.height(24.dp))

                    // Email/Password Fields
                    OutlinedTextField(
                        value = email,
                        onValueChange = { email = it; error = null; isSuccessMessage = false },
                        label = { Text("Email Address") },
                        modifier = Modifier.fillMaxWidth(),
                        leadingIcon = { Icon(Icons.Default.Email, null) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        singleLine = true,
                        shape = RoundedCornerShape(16.dp)
                    )

                    Spacer(Modifier.height(16.dp))

                    OutlinedTextField(
                        value = password,
                        onValueChange = { password = it; error = null; isSuccessMessage = false },
                        label = { Text("Password") },
                        modifier = Modifier.fillMaxWidth(),
                        leadingIcon = {
                            val isPasswordSecure = password.length >= 6
                            if (isSignUp && !isPasswordSecure) {
                                Icon(
                                    Icons.Default.LockOpen, 
                                    contentDescription = null,
                                    tint = MaterialTheme.colorScheme.error
                                )
                            } else {
                                Icon(Icons.Default.Lock, null)
                            }
                        },
                        trailingIcon = {
                            IconButton(onClick = { passwordVisible = !passwordVisible }) {
                                Icon(if (passwordVisible) Icons.Default.Visibility else Icons.Default.VisibilityOff, null)
                            }
                        },
                        visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                        singleLine = true,
                        shape = RoundedCornerShape(16.dp)
                    )

                    if (error != null) {
                        Text(
                            text = error!!,
                            color = if (isSuccessMessage) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.error,
                            style = MaterialTheme.typography.bodySmall,
                            modifier = Modifier.padding(top = 8.dp).fillMaxWidth(),
                            textAlign = TextAlign.Start
                        )
                    }

                    Spacer(Modifier.height(24.dp))

                    Button(
                        onClick = {
                            if (email.isBlank() || password.isBlank()) {
                                error = "Please fill in all fields."
                                isSuccessMessage = false
                                return@Button
                            }
                            if (isSignUp && password.length < 6) {
                                error = "Password must be at least 6 characters."
                                isSuccessMessage = false
                                return@Button
                            }
                            
                            isLoading = true
                            error = null
                            isSuccessMessage = false
                            authJob = scope.launch {
                                try {
                                    if (isSignUp) {
                                        mainViewModel.signUpWithEmail(email, password)
                                    } else {
                                        mainViewModel.signInWithEmail(email, password)
                                    }
                                } catch (e: Throwable) {
                                    val message = e.message ?: ""
                                    error = when {
                                        message.contains("email-already-in-use") -> "This email is already registered. Try signing in instead."
                                        message.contains("weak-password") -> "Password is too weak. Please use at least 6 characters."
                                        message.contains("user-not-found") || message.contains("wrong-password") || message.contains("invalid-credential") -> 
                                            "Invalid email or password."
                                        message.contains("invalid-email") -> "Please enter a valid email address."
                                        message.contains("too-many-requests") -> "Too many failed attempts. Please try again later."
                                        else -> "Authentication failed. Please try again."
                                    }
                                    isSuccessMessage = false
                                } finally {
                                    isLoading = false
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth().height(56.dp),
                        shape = RoundedCornerShape(16.dp),
                        enabled = !isLoading
                    ) {
                        if (isLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(24.dp), strokeWidth = 2.dp, color = MaterialTheme.colorScheme.onPrimary)
                        } else {
                            Text(if (isSignUp) "Create Account" else "Sign In", fontWeight = FontWeight.Bold)
                        }
                    }

                    Spacer(modifier = Modifier.height(16.dp))

                    // Forgot Password
                    if (!isSignUp) {
                        TextButton(
                            onClick = {
                                if (email.isBlank()) {
                                    error = "Enter your email to reset password."
                                    isSuccessMessage = false
                                    return@TextButton
                                }
                                scope.launch {
                                    try {
                                        mainViewModel.sendPasswordResetEmail(email)
                                        error = "Reset email sent! Please check your inbox."
                                        isSuccessMessage = true
                                    } catch (e: Throwable) {
                                        val message = e.message ?: ""
                                        error = if (message.contains("user-not-found")) "No account found with this email." else "Failed to send reset email."
                                        isSuccessMessage = false
                                    }
                                }
                            }
                        ) {
                            Text("Forgot Password?", style = MaterialTheme.typography.labelLarge)
                        }
                    }

                    // Toggle Login/Signup
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            if (isSignUp) "Already have an account?" else "Don't have an account?",
                            style = MaterialTheme.typography.bodySmall
                        )
                        TextButton(onClick = { isSignUp = !isSignUp; error = null; isSuccessMessage = false }) {
                            Text(if (isSignUp) "Sign In" else "Sign Up", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
        }
    }
}

